import { ArrowDown, ArrowUp, ArrowUpDown, Edit2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Quote } from '../../../types';
import { formatCurrency, getStatusBadgeVariant, getStatusLabel, SortColumn, SortDirection } from './quoteHelpers';

interface QuotesTableProps {
  quotes: Quote[];
  sortColumn: SortColumn | null;
  sortDirection: SortDirection;
  onSort: (column: SortColumn) => void;
  onOpenDetails: (quote: Quote) => void;
}

function SortIcon({ column, sortColumn, sortDirection }: { column: SortColumn; sortColumn: SortColumn | null; sortDirection: SortDirection }) {
  if (sortColumn !== column) {
    return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
  }
  return sortDirection === 'asc'
    ? <ArrowUp className="ml-1 h-3 w-3" />
    : <ArrowDown className="ml-1 h-3 w-3" />;
}

export function QuotesTable({ quotes, sortColumn, sortDirection, onSort, onOpenDetails }: QuotesTableProps) {
  return (
    <div className="hidden md:block overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className="whitespace-nowrap cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('quoteNumber')}
            >
              <div className="flex items-center">
                Numero
                <SortIcon column="quoteNumber" sortColumn={sortColumn} sortDirection={sortDirection} />
              </div>
            </TableHead>
            <TableHead
              className="min-w-[120px] max-w-[200px] cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('customerName')}
            >
              <div className="flex items-center">
                Cliente
                <SortIcon column="customerName" sortColumn={sortColumn} sortDirection={sortDirection} />
              </div>
            </TableHead>
            <TableHead
              className="min-w-[120px] max-w-[200px] cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('customerEmail')}
            >
              <div className="flex items-center">
                E-mail
                <SortIcon column="customerEmail" sortColumn={sortColumn} sortDirection={sortDirection} />
              </div>
            </TableHead>
            <TableHead
              className="text-right whitespace-nowrap cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('totalAmount')}
            >
              <div className="flex items-center justify-end">
                Valor Total
                <SortIcon column="totalAmount" sortColumn={sortColumn} sortDirection={sortDirection} />
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('status')}
            >
              <div className="flex items-center">
                Status
                <SortIcon column="status" sortColumn={sortColumn} sortDirection={sortDirection} />
              </div>
            </TableHead>
            <TableHead
              className="hidden text-right md:table-cell whitespace-nowrap cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('createdAt')}
            >
              <div className="flex items-center justify-end">
                Criado em
                <SortIcon column="createdAt" sortColumn={sortColumn} sortDirection={sortDirection} />
              </div>
            </TableHead>
            <TableHead className="text-right">Acoes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {quotes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-sm">
                Nenhum orcamento encontrado.
              </TableCell>
            </TableRow>
          ) : (
            quotes.map((quote: Quote) => (
              <TableRow key={quote.id}>
                <TableCell className="font-medium whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {quote.quoteNumber}
                    {!quote.isViewed && (
                      <Badge variant="destructive" className="text-xs px-1.5 py-0">Novo</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[200px]">
                  <div className="truncate" title={quote.customerName}>
                    {quote.customerName}
                  </div>
                  {quote.customerCompany && (
                    <span className="block text-xs text-muted-foreground truncate" title={quote.customerCompany}>
                      {quote.customerCompany}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm max-w-[200px]">
                  <span className="truncate block" title={quote.customerEmail}>
                    {quote.customerEmail}
                  </span>
                </TableCell>
                <TableCell className="text-right font-medium whitespace-nowrap">
                  {formatCurrency(quote.totalAmount)}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(quote.status)}>
                    {getStatusLabel(quote.status)}
                  </Badge>
                </TableCell>
                <TableCell className="hidden text-right md:table-cell text-xs text-muted-foreground">
                  {new Date(quote.createdAt).toLocaleDateString('pt-BR')}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOpenDetails(quote)}
                    title="Ver detalhes"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
